import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Check, X } from "lucide-react";
import { CANALES } from "@/lib/constants";
import { formatFechaCorta } from "@/lib/agenda";
import { nombreTourDisplay } from "@/lib/tourDisplay";

export default function PendientesVencidasDialog({ open, onOpenChange, onChanged }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ total: 0, por_canal: {}, items: [] });
  const [actuandoId, setActuandoId] = useState(null);

  const cargar = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/reservas/pendientes-vencidas");
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) cargar();
  }, [open]);

  const decidir = async (r, nuevoEstado) => {
    setActuandoId(r.id);
    try {
      await api.post(`/reservas/${r.id}/estado`, {
        nuevo_estado: nuevoEstado,
        motivo: "Revisión de pendiente vencida (tour ya pasó).",
      });
      toast.success(nuevoEstado === "confirmada" ? "Reserva confirmada." : "Reserva cancelada.");
      cargar();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setActuandoId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="pendientes-vencidas-dialog">
        <DialogHeader>
          <DialogTitle>Pendientes vencidas (fecha del tour ya pasó)</DialogTitle>
          <DialogDescription>
            Reservas que quedaron "Pendiente" y cuya fecha de tour ya pasó sin confirmarse ni cancelarse.
            Separadas del backlog normal de pendientes futuros para que las revises acá.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2" data-testid="pendientes-vencidas-resumen">
              <span className="text-sm font-semibold text-slate-700">Total: {data.total}</span>
              {Object.entries(data.por_canal || {}).map(([canal, n]) => (
                <span
                  key={canal}
                  className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border bg-slate-50 text-slate-600 border-slate-200"
                >
                  {CANALES[canal] || canal}: {n}
                </span>
              ))}
            </div>
            <div className="max-h-[50vh] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Código</TableHead>
                    <TableHead>Tour</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Pasajero</TableHead>
                    <TableHead>Canal</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-6">
                      No hay pendientes vencidas. Todo en orden.
                    </TableCell></TableRow>
                  ) : (
                    data.items.map((r) => (
                      <TableRow key={r.id} data-testid={`pendiente-vencida-row-${r.id}`}>
                        <TableCell className="text-xs">{r.codigo_booking || "—"}</TableCell>
                        <TableCell className="text-xs">{r.tour_nombre ? nombreTourDisplay(r.tour_nombre, r.modalidad) : (r.producto_raw || "—")}</TableCell>
                        <TableCell className="text-xs">{formatFechaCorta(r.fecha)} {r.hora}</TableCell>
                        <TableCell className="text-xs">{(r.pasajero || {}).nombre || "—"}</TableCell>
                        <TableCell className="text-xs">{CANALES[(r.sources || [{}])[0]?.canal] || (r.sources || [{}])[0]?.canal || "—"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="icon" variant="ghost" className="h-7 w-7 text-emerald-600"
                              disabled={actuandoId === r.id}
                              onClick={() => decidir(r, "confirmada")}
                              data-testid={`pendiente-vencida-confirmar-${r.id}`}
                              title="Confirmar"
                            >
                              {actuandoId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                            </Button>
                            <Button
                              size="icon" variant="ghost" className="h-7 w-7 text-red-600"
                              disabled={actuandoId === r.id}
                              onClick={() => decidir(r, "cancelada")}
                              data-testid={`pendiente-vencida-cancelar-${r.id}`}
                              title="Cancelar"
                            >
                              {actuandoId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
